/**
 * SubmissionComplete - Confirmation page after successful submission
 * Route: /i/:token/complete
 *
 * Informs the visitor that their submission is pending site manager sign-off.
 * Once approved the manager triggers an email with a welcome link back to
 * /i/:token/approved where the visitor can view their digital pass.
 */

import { useLocation } from 'react-router-dom';

export default function SubmissionComplete() {
  const location = useLocation();
  const submissionData = location.state;

  const referenceCode = submissionData?.reference || 'N/A';
  const submittedAt = submissionData?.submitted_at
    ? new Date(submissionData.submitted_at)
    : new Date();

  return (
    <div className="max-w-2xl mx-auto space-y-6">

      {/* ── Success header ──────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-7 text-white">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold">Induction Submitted!</h1>
              <p className="mt-1 text-blue-100 text-sm">
                Your answers and signature have been received successfully.
              </p>
            </div>
          </div>
        </div>

        {/* Reference + timestamp */}
        <div className="px-8 py-5 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-6 text-sm text-gray-600">
          <div>
            Reference: <span className="font-mono font-semibold text-gray-900">{referenceCode}</span>
          </div>
          <div>
            Submitted:{' '}
            <span className="font-medium text-gray-900">
              {submittedAt.toLocaleString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-6 space-y-6">

          {/* What happens next — timeline */}
          <div>
            <h2 className="text-base font-semibold text-gray-900 mb-4">What happens next?</h2>
            <ol className="space-y-5">
              <li className="flex items-start gap-4">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-yellow-100 flex items-center justify-center">
                  <span className="text-sm font-bold text-yellow-700">1</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Site Manager Review</p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    A site manager will review your answers and signature before your pass is issued.
                    You will not be able to access the site until this step is complete.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-sm font-bold text-blue-700">2</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Approval Email</p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Once approved, you will receive an email confirming your pass has been issued.
                    The email contains a personal link to your welcome page and digital pass.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-sm font-bold text-green-700">3</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Welcome &amp; Digital Pass</p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Your welcome page shows your QR code pass. Present it at the site entry kiosk
                    to check in and out during your visit.
                  </p>
                </div>
              </li>
            </ol>
          </div>

          {/* Email callout */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 flex items-start gap-3">
            <svg className="flex-shrink-0 h-5 w-5 text-amber-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-amber-800">Check your email</p>
              <p className="text-sm text-amber-700 mt-1">
                Keep an eye on your inbox — including spam. Your approval email will contain a
                direct link to your welcome page and QR pass. You do not need to return to
                this page.
              </p>
            </div>
          </div>

          {/* Current status */}
          <div className="flex items-center gap-3 py-3 border-t border-gray-100">
            <div className="h-3 w-3 rounded-full bg-yellow-400 animate-pulse" />
            <p className="text-sm text-gray-600">
              Status: <span className="font-semibold text-yellow-700">Pending site manager review</span>
            </p>
          </div>

        </div>
      </div>

      <div className="text-center">
        <p className="text-sm text-gray-500">
          You can safely close this window. Your reference is{' '}
          <span className="font-mono font-semibold text-gray-700">{referenceCode}</span>.
        </p>
      </div>

    </div>
  );
}
